import { Checkbox, Popover, Textarea, TextInput } from "@mantine/core";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  CalendarDays,
  Check,
  ChevronDown,
  Circle,
  CircleCheck,
  Flag,
  FlaskConical,
  LoaderCircle,
  Search,
  Tag,
  UserRound,
  X,
} from "lucide-react";
import {
  disciplines,
  disciplineNames,
  statuses,
  statusNames,
  type Member,
  type Priority,
  type Project,
  type TaskOptions,
  type TaskSummary,
  type User,
} from "../shared/types";
import { taskHandlers, type TaskFlowAction } from "../shared/task-flow";
import { assigneeName } from "./AssigneePicker";
import {
  LabelPicker,
  TaskTypeBadge,
  TaskTypePicker,
} from "./TaskClassification";
import { Button, Sheet } from "./ui";
import { useTelegramBackButton } from "./telegram";

export const taskPriorityNames: Record<Priority, string> = {
  urgent: "紧急",
  high: "高优先级",
  medium: "普通",
  low: "低优先级",
};
export function TaskStatusIcon({ status }: { status: TaskSummary["status"] }) {
  const Icon = {
    todo: Circle,
    in_progress: LoaderCircle,
    in_review: FlaskConical,
    done: CircleCheck,
    cancelled: X,
  }[status];
  return (
    <Icon
      size={15}
      className={`task-status-glyph ${status}`}
      aria-hidden="true"
    />
  );
}
export function TaskAvatar({ user }: { user: User }) {
  return (
    <span className="task-avatar" title={assigneeName(user)}>
      {user.name.slice(0, 1)}
    </span>
  );
}
export const dateLabel = (value: string | null) =>
  value
    ? new Date(`${value}T12:00:00`).toLocaleDateString("zh-CN", {
        month: "numeric",
        day: "numeric",
      })
    : "无截止日期";
export type TaskCommit = (input: Record<string, unknown>) => Promise<unknown>;
type Field = "status" | "people" | "priority" | "type" | "date" | "labels";
const fieldNames: Record<Field, string> = {
  status: "状态",
  people: "处理人",
  priority: "优先级",
  type: "任务类型",
  date: "截止日期",
  labels: "标签",
};

export function TaskField({
  task,
  project,
  members,
  currentUserId,
  kind,
  onCommit,
  disabled = false,
  compact = false,
  options,
  action,
  mobile = false,
  onEditingChange,
}: {
  task: TaskSummary;
  project: Project;
  members: Member[];
  currentUserId?: string;
  kind: Field;
  onCommit: TaskCommit;
  disabled?: boolean;
  compact?: boolean;
  options?: TaskOptions;
  action?: TaskFlowAction;
  mobile?: boolean;
  onEditingChange?: (id: string, opened: boolean) => void;
}) {
  const editorId = useId();
  const [opened, setOpened] = useState(false);
  const [phase, setPhase] = useState<
    "choose" | "testing" | "return" | "cancel" | "complete"
  >("choose");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const editRevision = useRef(task.revision);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [issueType, setIssueType] = useState<string | null>(null);
  useEffect(() => {
    if (!opened) return;
    onEditingChange?.(editorId, true);
    return () => onEditingChange?.(editorId, false);
  }, [opened, editorId, onEditingChange]);
  useTelegramBackButton(
    opened,
    () => {
      if (!busy) setOpened(false);
    },
    10,
  );
  const testing = task.status === "in_review";
  const closed = ["done", "cancelled"].includes(task.status);
  const handlers = closed ? task.assignees : taskHandlers(task, members);
  const names = handlers.map(assigneeName).join("、");
  const finish = async (input: Record<string, unknown>) => {
    if (submitting.current || disabled) return;
    const unchanged = Object.entries(input).every(([field, value]) => {
      const current =
        field === "assignee_ids"
          ? task.assignees.map((p) => p.id)
          : field === "tester_ids"
            ? task.testers.map((p) => p.id)
            : (task as any)[field];
      return JSON.stringify(current) === JSON.stringify(value);
    });
    if (unchanged) {
      setOpened(false);
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      await onCommit({ ...input, expected_revision: editRevision.current });
      setOpened(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "修改失败，请重试");
      setOpened(true);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  const initialize = () => {
    editRevision.current = task.revision;
    setPhase("choose");
    setError("");
    setSearch("");
    setCategory("");
    setNote("");
    setPeople((testing ? task.testers : task.assignees).map((p) => p.id));
    setDate(task.due_date || "");
    setLabels(task.labels);
    setIssueType(task.issue_type);
  };
  const chooseStatus = (status: TaskSummary["status"]) => {
    if (status === task.status) {
      setOpened(false);
      return;
    }
    setNote("");
    setError("");
    if (status === "in_review") {
      setPeople(task.testers.map((p) => p.id));
      setPhase("testing");
    } else if (status === "in_progress" && testing) setPhase("return");
    else if (status === "cancelled") setPhase("cancel");
    else if (status === "done") setPhase("complete");
    else void finish({ status });
  };
  const open = () => {
    if (disabled) return;
    initialize();
    if (action) {
      if (["todo", "in_progress"].includes(action.target) && !action.note) {
        void finish({ status: action.target });
        return;
      }
      chooseStatus(action.target);
    }
    setOpened(true);
  };
  const pickingTester = phase === "testing" || (kind === "people" && testing);
  const candidates = [
    ...new Map(
      [...members, ...(pickingTester ? task.testers : task.assignees)].map(
        (p) => [p.id, p],
      ),
    ).values(),
  ].filter(
    (p) =>
      people.includes(p.id) ||
      !pickingTester ||
      members.some((m) => m.id === p.id && m.role !== "viewer"),
  );
  const selectedPeople = candidates.filter((p) => people.includes(p.id));
  const testers = taskHandlers(
    { ...task, status: "in_review", testers: selectedPeople },
    members,
  );
  const assignedColumn = pickingTester ? "tester_ids" : "assignee_ids";
  const self = candidates.find((p) => p.id === currentUserId);
  const matchedPeople = candidates.filter(
    (p) =>
      (!category ||
        members
          .find((m) => m.id === p.id)
          ?.disciplines.includes(category as any)) &&
      `${p.name} ${p.github_login || ""} ${p.username || ""}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
  );
  const notify = (selected: User[]) => {
    if (!project.direct_notifications) return "项目已关闭私聊通知。";
    if (!selected.length) return "尚未指定接手人。";
    const missing = selected.filter(
      (p) => !members.some((m) => m.id === p.id && m.telegram_id),
    );
    return `将通过机器人私聊通知 ${selected.map(assigneeName).join("、")}。${missing.length ? `${missing.map(assigneeName).join("、")} 需先连接 Telegram 并启动机器人。` : ""}`;
  };
  const pickSelf = () => {
    if (self)
      void finish({
        [assignedColumn]: [...new Set([...people, self.id])].slice(0, 10),
      });
  };
  let value: ReactNode;
  if (kind === "status")
    value = (
      <>
        <TaskStatusIcon status={task.status} />
        <span>{statusNames[task.status]}</span>
      </>
    );
  else if (kind === "people")
    value = (
      <>
        {handlers.length ? (
          <span className="task-avatar-stack">
            {handlers.slice(0, 2).map((p) => (
              <TaskAvatar key={p.id} user={p} />
            ))}
          </span>
        ) : (
          <UserRound size={15} />
        )}
        <span>
          {compact && handlers.length > 1
            ? `${assigneeName(handlers[0])} +${handlers.length - 1}`
            : names || (testing ? "待指定测试" : "待分配")}
        </span>
      </>
    );
  else if (kind === "priority")
    value = (
      <>
        <Flag size={14} className={`task-priority-glyph ${task.priority}`} />
        <span>{taskPriorityNames[task.priority]}</span>
      </>
    );
  else if (kind === "date")
    value = (
      <>
        <CalendarDays size={15} />
        <span>{dateLabel(task.due_date)}</span>
      </>
    );
  else if (kind === "type")
    value = (
      <>
        <TaskTypeBadge name={task.issue_type} />
        {!task.issue_type ? <span>无类型</span> : null}
      </>
    );
  else
    value = (
      <>
        <Tag size={15} />
        <span>{task.labels.length ? task.labels.join("、") : "添加标签"}</span>
      </>
    );
  const peopleEditor = (
    <>
      <TextInput
        aria-label="搜索可分配成员"
        placeholder="搜索姓名或账号"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<Search size={16} />}
        autoFocus={!mobile}
      />
      <div className="task-people-filter" aria-label="按成员分类筛选">
        {[
          { id: "", name: "全部" },
          ...disciplines.map((d) => ({ id: d, name: disciplineNames[d] })),
        ].map((item) => (
          <Button
            key={item.id}
            aria-pressed={category === item.id}
            onClick={() => setCategory(item.id)}
          >
            {item.name}
          </Button>
        ))}
      </div>
      <div className="task-member-options">
        {matchedPeople.map((p) => (
          <Checkbox
            key={p.id}
            label={
              <>
                <strong>{assigneeName(p)}</strong>
                {p.name !== p.github_login ? <small>{p.name}</small> : null}
                {pickingTester &&
                !members.some((m) => m.id === p.id && m.role !== "viewer") ? (
                  <small>已无测试权限，请移除或更换</small>
                ) : null}
              </>
            }
            checked={people.includes(p.id)}
            disabled={busy || (people.length >= 10 && !people.includes(p.id))}
            onChange={() =>
              setPeople((ids) =>
                ids.includes(p.id)
                  ? ids.filter((id) => id !== p.id)
                  : [...ids, p.id],
              )
            }
          />
        ))}
        {!matchedPeople.length ? (
          <p className="task-field-hint">没有匹配的成员，请调整搜索或分类。</p>
        ) : null}
      </div>
      <p className="task-field-hint">
        {people.length
          ? `已选：${selectedPeople.map(assigneeName).join("、")}`
          : pickingTester
            ? `未指定时由项目测试人员接手：${testers.map(assigneeName).join("、") || "暂无"}`
            : "可以分配给多人，也可以保持未分配。"}
      </p>
    </>
  );
  const primary = (
    text: string,
    input: Record<string, unknown>,
    blocked = false,
  ) => (
    <Button
      className="button primary"
      disabled={busy || disabled || blocked}
      onClick={() => void finish(input)}
    >
      {busy ? <LoaderCircle size={15} className="loading-spin" /> : null}
      {text}
    </Button>
  );
  const editorTitle =
    phase === "testing"
      ? "提交测试"
      : phase === "return"
        ? "退回开发"
        : phase === "cancel"
          ? "取消任务"
          : phase === "complete"
            ? testing
              ? "测试通过并完成"
              : "完成任务"
            : kind === "people"
              ? testing
                ? "分配测试人员"
                : "分配任务"
              : fieldNames[kind];
  const trigger = (
    <Button
      className={
        action
          ? "button primary task-next-action"
          : `task-field-trigger ${compact ? "compact" : ""} field-${kind}`
      }
      onClick={open}
      aria-haspopup="dialog"
      aria-expanded={opened}
      disabled={disabled || busy}
      title={kind === "people" ? names || "点击分配人员" : undefined}
      aria-label={
        action
          ? action.label
          : `修改 T-${task.number} 的${fieldNames[kind]}：${kind === "people" ? names || "待分配" : kind === "status" ? statusNames[task.status] : "点击编辑"}`
      }
    >
      {action ? action.label : value}
      {!action && !disabled ? (
        <ChevronDown size={12} className="task-field-chevron" />
      ) : null}
    </Button>
  );
  const editor = (
    <div className={mobile ? "task-field-sheet" : undefined}>
      {!mobile ? (
        <div className="task-field-menu-heading">
          <strong>{editorTitle}</strong>
          <Button
            className="icon-button"
            aria-label="关闭属性编辑"
            disabled={busy}
            onClick={() => setOpened(false)}
          >
            <X size={16} />
          </Button>
        </div>
      ) : null}
      {phase === "choose" && kind === "status" ? (
        <div className="task-choice-list">
          {statuses.map((s) => (
            <Button key={s} disabled={busy} onClick={() => chooseStatus(s)}>
              <TaskStatusIcon status={s} />
              <span>{statusNames[s]}</span>
              {s === task.status ? <Check size={15} /> : null}
            </Button>
          ))}
        </div>
      ) : null}
      {phase === "testing" || (phase === "choose" && kind === "people")
        ? peopleEditor
        : null}
      {phase === "choose" && kind === "priority" ? (
        <div className="task-choice-list">
          {Object.entries(taskPriorityNames).map(([p, name]) => (
            <Button
              key={p}
              disabled={busy}
              onClick={() => {
                if (p === task.priority) setOpened(false);
                else void finish({ priority: p });
              }}
            >
              <Flag size={15} className={`task-priority-glyph ${p}`} />
              <span>{name}</span>
              {p === task.priority ? <Check size={15} /> : null}
            </Button>
          ))}
        </div>
      ) : null}
      {phase === "choose" && kind === "date" ? (
        <TextInput
          aria-label="任务截止日期"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
        />
      ) : null}
      {phase === "choose" && kind === "type" && options ? (
        <TaskTypePicker
          value={issueType}
          onChange={setIssueType}
          options={options}
        />
      ) : null}
      {phase === "choose" && kind === "labels" && options ? (
        <LabelPicker
          value={labels}
          onChange={setLabels}
          options={options.labels}
        />
      ) : null}
      {phase === "return" || phase === "cancel" ? (
        <>
          <p className="task-field-hint">
            {phase === "return"
              ? `交回任务负责人：${task.assignees.map(assigneeName).join("、") || "待分配"}`
              : "任务将结束处理，关联的 GitHub Issue 会标记为取消。"}
          </p>
          <Textarea
            aria-label={
              phase === "return" ? "退回说明（选填）" : "取消原因（选填）"
            }
            placeholder={
              phase === "return"
                ? "说明未通过的原因（选填）"
                : "取消原因（选填）"
            }
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            maxLength={2000}
            autosize
            minRows={2}
          />
        </>
      ) : null}
      {phase === "complete" ? (
        <p className="task-field-hint">
          {project.repo_id
            ? "任务将标记为已完成，并同步关闭 GitHub Issue。"
            : "任务将标记为已完成。"}
        </p>
      ) : null}
      {phase === "testing" ? (
        <p className="task-field-hint">
          {notify(testers)}原任务负责人保留。
          {!testers.length ? "请先选择有效测试接手人。" : ""}
        </p>
      ) : null}
      {error ? (
        <p className="task-field-error" role="alert">
          {error}
        </p>
      ) : null}
      {phase !== "choose" || !["status", "priority"].includes(kind) ? (
        <div className="task-field-menu-footer">
          {phase === "testing" ? (
            primary(
              "提交测试",
              { status: "in_review", tester_ids: people },
              !testers.length,
            )
          ) : phase === "return" ? (
            primary("退回开发", {
              status: "in_progress",
              ...(note.trim() ? { transition_note: note.trim() } : {}),
            })
          ) : phase === "cancel" ? (
            primary("取消任务", {
              status: "cancelled",
              ...(note.trim() ? { transition_note: note.trim() } : {}),
            })
          ) : phase === "complete" ? (
            primary(testing ? "测试通过并完成" : "完成任务", {
              status: "done",
            })
          ) : kind === "people" ? (
            <>
              {self && !people.includes(self.id) ? (
                <Button
                  className="text-button"
                  disabled={busy || people.length >= 10}
                  onClick={pickSelf}
                >
                  {people.length ? "加入我" : "分配给我"}
                </Button>
              ) : null}
              {primary("保存分配", { [assignedColumn]: people })}
            </>
          ) : kind === "date" ? (
            <>
              <Button
                className="text-button"
                disabled={busy}
                onClick={() => setDate("")}
              >
                清除
              </Button>
              {primary("保存日期", { due_date: date || null })}
            </>
          ) : kind === "type" ? (
            primary("保存类型", { issue_type: issueType })
          ) : kind === "labels" ? (
            primary("保存标签", { labels })
          ) : null}
        </div>
      ) : null}
    </div>
  );
  if (mobile)
    return (
      <>
        {trigger}
        {opened ? (
          <Sheet
            title={editorTitle}
            compact
            zIndex={600}
            closeOnEscape={!busy}
            onClose={() => {
              if (!busy) setOpened(false);
            }}
          >
            {editor}
          </Sheet>
        ) : null}
      </>
    );
  return (
    <Popover
      opened={opened}
      onChange={(value) => {
        if (!busy) setOpened(value);
      }}
      position="bottom-start"
      width={330}
      shadow="md"
      withinPortal
      trapFocus
      zIndex={600}
      closeOnEscape={!busy}
      classNames={{ dropdown: "task-field-menu" }}
    >
      <Popover.Target>{trigger}</Popover.Target>
      <Popover.Dropdown
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            if (!busy) setOpened(false);
          }
        }}
      >
        {editor}
      </Popover.Dropdown>
    </Popover>
  );
}
